/**
 * Impressum, Datenschutzerklärung und Nutzungsbedingungen (HM-061, OQ-033).
 *
 * Aufbau aus `templates/legal/Legal.dc.html` des Design-System-Projekts. Die
 * Nutzungsbedingungen hat der Entwurf nicht; sie stehen in derselben Sprache
 * und im selben Rahmen, weil ein drittes Dokument in einer vierten Gestalt
 * aussähe, als gehörte es woanders hin.
 *
 * **Übernommen ist der Aufbau, nicht jede Zusage** — dieselbe Regel wie beim
 * Dashboard (HM-030) und bei der Anmeldung (HM-059), und hier wiegt sie am
 * schwersten: Eine Datenschutzerklärung ist keine Beschreibung, sondern eine
 * Auskunft nach Art. 13 DSGVO. Was darin steht, muss das Produkt tun.
 *
 * Sieben Aussagen des Entwurfs hat diese Umsetzung deshalb geändert, jede gegen
 * den Code geprüft:
 *
 * | Entwurf                                            | Tatsächlich                                          |
 * | -------------------------------------------------- | ---------------------------------------------------- |
 * | unbestätigte Registrierung: 30 Tage                | 24 Stunden (`PENDING_REGISTRATION_MAX_AGE_SECONDS`)  |
 * | IP-Adresse der Code-Anforderung wird gespeichert   | `pending_registrations` führt keine IP               |
 * | bei jeder Anmeldung IP und Gerätekennung           | es gibt keine Sitzungstabelle mit diesen Feldern     |
 * | „Ihnen die aktiven Sitzungen anzeigen"             | die Ansicht gibt es nicht (seit HM-101 gibt es sie)  |
 * | Sicherheitsprotokoll: 90 Tage                      | `audit_log` hat keinen Löschlauf                     |
 * | Messwerte „selbst exportieren oder löschen"        | es gibt weder Export- noch Löschendpunkt             |
 * | „Löschen Sie eine Site …"                          | eine Anlage wird **archiviert**, nicht gelöscht      |
 *
 * Die letzten vier sind die gefährliche Sorte: Sie versprechen dem Nutzer mehr,
 * als er bekommt, und lesen sich dabei völlig plausibel. Es ist derselbe Fund
 * wie „die App für iOS und Android" in HM-059, nur in einem Text, an dem sich
 * jemand später festhält.
 *
 * **Die Anrede ist das informelle _du_, kleingeschrieben** — entschieden vom
 * Auftraggeber (ADR 0051), wie im übrigen Design-System.
 *
 * **Die Texte stehen seit HM-158 in `@hausmeter/legal`** (`app-documents.ts`), als
 * Daten: Die Mobile-App zeigt dieselben Dokumente nativ an, und zwei Fassungen
 * eines Rechtstexts wären zwei Zusagen. Hier steht nur noch, wie die Web-App sie
 * zeichnet — mit den Bausteinen aus `legal-layout.tsx`.
 */
import { APP_LEGAL_DOCUMENTS, type LegalBlock, type LegalText } from "@hausmeter/legal";
import { Fragment, type ReactNode } from "react";
import type { LegalDocument } from "../lib/router.js";
import {
  LegalCode,
  LegalExternal,
  LegalFacts,
  LegalLink,
  LegalList,
  LegalNote,
  LegalPage,
  LegalParagraph,
  LegalScreen,
  LegalSection,
  LegalTable,
} from "../components/legal-layout.js";

/**
 * Ein Stück Fließtext. `strong` im Hinweiskasten steht in `--text-strong`: Der
 * Kasten setzt seinen Text leiser, und „Kurz gefasst:" soll sich davon abheben.
 */
function Inline({ text, inNote = false }: { readonly text: LegalText; readonly inNote?: boolean }) {
  return (
    <>
      {text.map((part, index) => {
        if (typeof part === "string") return <Fragment key={index}>{part}</Fragment>;
        switch (part.kind) {
          case "strong":
            return inNote ? (
              <strong key={index} style={{ color: "var(--text-strong)" }}>{part.text}</strong>
            ) : (
              <strong key={index}>{part.text}</strong>
            );
          case "em":
            return <em key={index}>{part.text}</em>;
          case "code":
            return <LegalCode key={index}>{part.text}</LegalCode>;
          case "link":
            return <LegalExternal key={index} href={part.href}>{part.text}</LegalExternal>;
          case "document":
            return (
              <LegalLink key={index} to={{ kind: "legal", document: part.document }}>
                {part.text}
              </LegalLink>
            );
        }
      })}
    </>
  );
}

function Blocks({ blocks }: { readonly blocks: readonly LegalBlock[] }) {
  return (
    <>
      {blocks.map((block, index) => {
        switch (block.kind) {
          case "paragraph":
            return (
              <LegalParagraph key={index}>
                <Inline text={block.text} />
              </LegalParagraph>
            );
          case "list":
            return <LegalList key={index} items={block.items.map((item, i) => <Inline key={i} text={item} />)} />;
          case "note":
            return (
              <LegalNote key={index}>
                <Inline text={block.text} inNote />
              </LegalNote>
            );
          case "facts":
            return (
              <LegalFacts
                key={index}
                items={block.items.map((item) => ({
                  label: item.label,
                  lines: item.lines.map((line, i) => <Inline key={i} text={line} />),
                  ...(item.note !== undefined ? { note: item.note } : {}),
                }))}
              />
            );
          case "table":
            return (
              <LegalTable
                key={index}
                {...(block.head !== undefined ? { head: block.head } : {})}
                rows={block.rows.map(([left, right], i) => [<Inline key={`l${i}`} text={left} />, <Inline key={`r${i}`} text={right} />] as const)}
              />
            );
          case "section":
            return (
              <LegalSection key={index} title={block.title}>
                <Blocks blocks={block.blocks} />
              </LegalSection>
            );
        }
      })}
    </>
  );
}

/**
 * Titel und Unterzeile je Dokument — **an einer Stelle für beide Rahmen** und für
 * die Mobile-App: Die eigenständige Seite setzt sie in ihren Kartenkopf, das
 * Dashboard in seine Kopfzeile.
 */
export const LEGAL_META: Record<LegalDocument, { readonly title: string; readonly lead: string }> = APP_LEGAL_DOCUMENTS;

function Body({ document }: { readonly document: LegalDocument }): ReactNode {
  return <Blocks blocks={APP_LEGAL_DOCUMENTS[document].blocks} />;
}

/**
 * Die **eigenständige** Fassung — für jeden, der nicht angemeldet ist.
 *
 * Sie ist nicht der Rückfall, sondern die Pflichtfassung: § 5 DDG verlangt das
 * Impressum „ständig verfügbar", Art. 13 DSGVO die Erklärung **vor** der
 * Erhebung, und wer den Nutzungsbedingungen zustimmen soll, muss sie vorher
 * lesen können — alles drei, bevor es eine Sitzung gibt.
 */
export function LegalDocumentPage({ document }: { readonly document: LegalDocument }) {
  const meta = LEGAL_META[document];
  return (
    <LegalPage current={document} title={meta.title} lead={meta.lead}>
      <Body document={document} />
    </LegalPage>
  );
}

/**
 * Dieselben Texte **im Dashboard** — für einen angemeldeten Nutzer
 * (`ui_kits/dashboard/LegalScreen.jsx`). Derselbe Körper, ein anderer Rahmen.
 */
export function LegalDocumentScreen({ document }: { readonly document: LegalDocument }) {
  return (
    <LegalScreen current={document}>
      <Body document={document} />
    </LegalScreen>
  );
}
